import axios from 'axios';
import { AppAnnouncement } from '../models/AppAnnouncement';

const API_URL = process.env.REACT_APP_SERVER_URL + "/app/announcements";

const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('gpcwstoken_kw')}`,
});

export const AppAnnouncementService = {
    getAnnouncementsPaginated: async (
        page: number,
        limit: number,
        search?: string
    ): Promise<{ announcements: AppAnnouncement[]; total: number }> => {
        const params: Record<string, any> = { page, limit };
        if (search) params.search = search;
        const response = await axios.get(`${API_URL}/getAnnouncementsPaginated`, { params, headers: authHeader() });
        return response.data;
    },
    getAllAnnouncements: async (): Promise<AppAnnouncement[]> => {
        const response = await axios.get(`${API_URL}/getAllAnnouncements`, { headers: authHeader() });
        console.log(response);
        return response.data;
    },

    getRecentAnnouncements: async (limit: number = 5): Promise<AppAnnouncement[]> => {
        const response = await axios.get(`${API_URL}/getRecentAnnouncements`, { params: { limit }, headers: authHeader() });
        return response.data;
    },

    getAnnouncementById: async (id: number): Promise<AppAnnouncement> => {
        const response = await axios.get(`${API_URL}/getAnnouncementById/${id}`, { headers: authHeader() });
        return response.data;
    },

    uploadImage: async (file: File): Promise<{ success: boolean; file_url: string; file_type: string; original_name: string }> => {
        const form = new FormData();
        form.append('image', file);
        const response = await axios.post(`${API_URL}/upload-image`, form, {
            headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
        });
        return response.data;
    },

    uploadMultipleImages: async (files: File[]): Promise<{ success: boolean; files: Array<{ url: string; type: string; original_name: string }>; count: number }> => {
        const form = new FormData();
        files.forEach((file, index) => {
            form.append('images', file);
        });
        const response = await axios.post(`${API_URL}/upload-multiple-images`, form, {
            headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
        });
        return response.data;
    },

    createAnnouncement: async (
        announcement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        file?: File
    ) => {
        if (file) {
            const form = new FormData();
            form.append('title', announcement.title);
            form.append('content', announcement.content);
            if (announcement.created_user_id) form.append('created_user_id', announcement.created_user_id);
            form.append('image', file);
            const response = await axios.post(`${API_URL}/addWithImage`, form, {
                headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
            });
            return response.data;
        }
        const response = await axios.post(`${API_URL}/add`, announcement, { headers: authHeader() });
        return response.data;
    },

    createAnnouncementWithMultipleImages: async (
        announcement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        files: File[]
    ) => {
        const form = new FormData();
        form.append('title', announcement.title);
        form.append('content', announcement.content);
        if (announcement.created_user_id) form.append('created_user_id', announcement.created_user_id);

        files.forEach((file, index) => {
            form.append('images', file);
        });

        const response = await axios.post(`${API_URL}/addWithMultipleImages`, form, {
            headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
        });
        return response.data;
    },

    updateAnnouncement: async (
        id: number,
        announcement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        file?: File
    ) => {
        if (file) {
            const form = new FormData();
            form.append('title', announcement.title);
            form.append('content', announcement.content);
            if (announcement.created_user_id) form.append('created_user_id', announcement.created_user_id);
            form.append('image', file);
            const response = await axios.put(`${API_URL}/updateWithImage/${id}`, form, {
                headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
            });
            return response.data;
        }
        const response = await axios.put(`${API_URL}/update/${id}`, announcement, { headers: authHeader() });
        return response.data;
    },

    updateAnnouncementWithMultipleImages: async (
        id: number,
        announcement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        files: File[]
    ) => {
        const form = new FormData();
        form.append('title', announcement.title);
        form.append('content', announcement.content);
        if (announcement.created_user_id) form.append('created_user_id', announcement.created_user_id);

        files.forEach((file, index) => {
            form.append('images', file);
        });

        const response = await axios.put(`${API_URL}/updateWithMultipleImages/${id}`, form, {
            headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
        });
        return response.data;
    },

    deleteAnnouncement: async (id: number): Promise<void> => {
        await axios.delete(`${API_URL}/delete/${id}`, { headers: authHeader() });
    }
};
